/* Validation copy for the address fields. */

import { defineMessages } from "react-intl";

import type { AddressFormMessage } from "./AddressForm.types";

const labelMessages = defineMessages({
  countryTooltipTrigger: {
    id: "hosting.address_form.country_tooltip_trigger",
    defaultMessage: "Why can’t I change the country?",
  },
});

/** Accessible name for the "?" button beside a locked Country field. */
const countryTooltipTriggerMessage = labelMessages.countryTooltipTrigger;

/** Errors whose wording does not depend on the selected country. */
const addressFormMessages = defineMessages({
  selectCountry: {
    id: "hosting.address_form_errors.enter_country",
    defaultMessage: "Please select a country",
  },
  enterStreetAddress: {
    id: "hosting.address_form_errors.enter_street_address",
    defaultMessage: "Please enter a street address",
  },
}) satisfies Record<string, AddressFormMessage>;

// Shopify labels the city, zone and postal-code fields differently per country
// (`Country.labels`), and each variant has its own translated error. The keys below
// are those English labels, exactly as `@shopify/address` reports them.

const DEFAULT_CITY_LABEL = "City";

const cityMessageByLabel: Record<string, AddressFormMessage> = defineMessages({
  Area: {
    id: "hosting.address_form_errors.enter_area",
    defaultMessage: "Please enter an area",
  },
  City: {
    id: "hosting.address_form_errors.enter_city",
    defaultMessage: "Please enter a city",
  },
  "City/town": {
    id: "hosting.address_form_errors.enter_city_town",
    defaultMessage: "Please enter a city/town",
  },
  "City/ward/town/village": {
    id: "hosting.address_form_errors.enter_city_ward_town_village",
    defaultMessage: "Please enter a city/ward/town/village",
  },
  District: {
    id: "hosting.address_form_errors.enter_district",
    defaultMessage: "Please enter a district",
  },
  Suburb: {
    id: "hosting.address_form_errors.enter_suburb",
    defaultMessage: "Please enter a suburb",
  },
  Ward: {
    id: "hosting.address_form_errors.enter_ward",
    defaultMessage: "Please enter a ward",
  },
});

const DEFAULT_POSTAL_CODE_LABEL = "Postal code";

const postalCodeMessageByLabel: Record<string, AddressFormMessage> =
  defineMessages({
    "PIN code": {
      id: "hosting.address_form_errors.enter_valid_pin_code",
      defaultMessage: "Please enter a valid PIN code",
    },
    "Postal code": {
      id: "hosting.address_form_errors.enter_valid_postal_code",
      defaultMessage: "Please enter a valid postal code",
    },
    Postcode: {
      id: "hosting.address_form_errors.enter_valid_post_code",
      defaultMessage: "Please enter a valid post code",
    },
    "ZIP code": {
      id: "hosting.address_form_errors.enter_valid_zip_code",
      defaultMessage: "Please enter a valid zip code",
    },
  });

const DEFAULT_ZONE_LABEL = "State";

const zoneMessageByLabel: Record<string, AddressFormMessage> = defineMessages({
  Country: {
    id: "hosting.address_form_errors.select_country",
    defaultMessage: "Please select a country",
  },
  County: {
    id: "hosting.address_form_errors.select_county",
    defaultMessage: "Please select a county",
  },
  Emirate: {
    id: "hosting.address_form_errors.select_emirate",
    defaultMessage: "Please select an emirate",
  },
  Governorate: {
    id: "hosting.address_form_errors.select_governorate",
    defaultMessage: "Please select a governorate",
  },
  Prefecture: {
    id: "hosting.address_form_errors.select_prefecture",
    defaultMessage: "Please select a prefecture",
  },
  Province: {
    id: "hosting.address_form_errors.select_province",
    defaultMessage: "Please select a province",
  },
  Region: {
    id: "hosting.address_form_errors.select_region",
    defaultMessage: "Please select a region",
  },
  State: {
    id: "hosting.address_form_errors.select_state",
    defaultMessage: "Please select a state",
  },
  "State/territory": {
    id: "hosting.address_form_errors.state_territory",
    defaultMessage: "Please select a state/territory",
  },
});

export {
  DEFAULT_CITY_LABEL,
  DEFAULT_POSTAL_CODE_LABEL,
  DEFAULT_ZONE_LABEL,
  addressFormMessages,
  cityMessageByLabel,
  countryTooltipTriggerMessage,
  postalCodeMessageByLabel,
  zoneMessageByLabel,
};
